import {
  Autocomplete,
  Box,
  Container,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Stack,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import BackBtn from "../../widgets/BackBtn";
import PageTitle from "../../widgets/PageTitle";
import useApiEp from "../../hooks/useApiEp";
import {
  exportToExcel,
  getContactColumnNames,
  getErrorResponse,
  getHeader,
} from "../../utils/util";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import SectionLabel from "../../widgets/SectionLabel";
import ImportOrManual from "../../widgets/ImportOrManual";
import ExcelImportDialog from "../../widgets/ExcelImportDialog";
import DynamicTableDialog from "../../widgets/DynamicTableDialog";

const AddContactToGroup = () => {
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [open, setOpen] = useState(false);
  const [openDialogForm, setOpenDialogForm] = useState(false);
  const [contactData, setContactData] = useState({
    data: null,
    isLoading: false,
    error: null,
  });

  // Fetch contact groups
  const {
    data: contactList,
    isLoading: loadingList,
    isError: isListError,
    error: listError,
  } = useApiEp("/user/group-list");

  // Fetch contacts when group is selected
  useEffect(() => {
    if (!selectedGroup) return;

    const fetchContacts = async () => {
      setContactData((prev) => ({ ...prev, isLoading: true, error: null }));
      try {
        const response = await axios.get(
          `${API_ENDPOINT}/user/get-contact-schema?groupName=${encodeURIComponent(
            selectedGroup
          )}`,
          getHeader()
        );
        const schemaList = getContactColumnNames(response.data);
        setContactData({ data: schemaList, error: null });
      } catch (err) {
        setContactData({
          data: null,
          error: getErrorResponse(err) || "Failed to load contacts",
        });
      } finally {
        setContactData((prev) => ({ ...prev, isLoading: false }));
      }
    };
    fetchContacts();
  }, [selectedGroup]);

  const handleGroupChange = (_, newValue) => {
    setSelectedGroup(newValue);
    setContactData({ data: null, isLoading: false, error: null }); // Reset contact data
  };

  return (
    <Container maxWidth="md" sx={{ mt: 3 }}>
      <BackBtn />
      <PageTitle>Add Contact List to group</PageTitle>

      {/* Group Selection */}
      <Autocomplete
        size="small"
        loading={loadingList}
        sx={{ minWidth: 164, my: 3 }}
        options={contactList || []}
        value={selectedGroup}
        onChange={handleGroupChange}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Group Name"
            helperText={
              selectedGroup ? "" : "Select contact group to view members"
            }
          />
        )}
      />

      {/* Contact Loading State */}
      {contactData.isLoading && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress size={24} />
        </Box>
      )}

      {/* Contact Error State */}
      {contactData.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {contactData.error}
        </Alert>
      )}
      {selectedGroup && (
        <Box>
          <SectionLabel sx={{ my: 1 }}>Column names</SectionLabel>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {contactData?.data?.map((item) => {
              return <Chip label={item} variant="outlined" clickable />;
            })}
          </Stack>
          <ImportOrManual
            onDownloadTemplate={() => {
              if (selectedGroup && contactData?.data?.length > 0) {
                exportToExcel(
                  [contactData.data],
                  "import_contact_template.xlsx",
                  true
                );
              }
            }}
            onImportExcel={() => {
              setOpen(true);
            }}
            onManualAdd={() => {
              setOpenDialogForm(true);
            }}
          />
          <ExcelImportDialog
            open={open}
            onClose={() => setOpen(false)}
            url={`${API_ENDPOINT}/user/import-excel-to-contact-group`}
            validateInput={(rows) => {
              if (!Array.isArray(rows)) {
                return "Invalid data!";
              }
              if (rows.length < 1) {
                return "Atleast one line item should be there!";
              }
              const columns = Object.keys(rows[0]);
              if (!columns.includes("phoneNumber")) {
                return "Phone number column must be included!";
              }
              const isEmptyPhone =
                rows.filter((item) => !String(item.phoneNumber)).length > 0;
              if (isEmptyPhone) {
                return "Phone number should be non empty!";
              }
              return null;
            }}
            getPayload={(rows) => {
              return {
                contacts: rows,
                groupName: selectedGroup.trim(),
              };
            }}
          />
          <DynamicTableDialog
            open={openDialogForm}
            onClose={() => setOpenDialogForm(false)}
            columnNames={contactData?.data || []}
            title="Add Contacts"
            validateInput={(rows) => {
              if (!Array.isArray(rows)) {
                return "Invalid data!";
              }
              if (rows.length < 1) {
                return "Atleast one line item should be there!";
              }
              const columns = Object.keys(rows[0]);
              if (!columns.includes("phoneNumber")) {
                return "Phone number column must be included!";
              }
              const isEmptyPhone =
                rows.filter((item) => !item.phoneNumber).length > 0;
              if (isEmptyPhone) {
                return "Phone number should be non empty!";
              }
              return null;
            }}
            url={`${API_ENDPOINT}/user/import-excel-to-contact-group`}
            getPayload={(rows) => {
              return {
                contacts: rows,
                groupName: selectedGroup.trim(),
              };
            }}
          />
        </Box>
      )}
    </Container>
  );
};

export default AddContactToGroup;
